import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { BorrowerDetail, FinancialYear } from "../../../models/dto/cat15/cat15.types";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import { formatDate, formatNumber } from "../format";

interface Props {
  borrower: BorrowerDetail;
  onAddYear: () => void;
  onEditYear: (row: FinancialYear) => void;
}

/** Financial Data tab — Figma node 6100:51308. */
const DetailFinancial = ({ borrower, onAddYear, onEditYear }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rows = borrower.financials.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontSize="18px" fontWeight={600} color="var(--dark)">
          {t("Bank-Entered-Financial")}
        </Typography>
        <Button
          variant="contained"
          color="green"
          onClick={onAddYear}
          sx={{ padding: "8px 12px" }}
        >
          + {t("Add-Year")}
        </Button>
      </Box>
      <FeTableWrap>
        <FeTableScroll>
        <FeTable>
          <TableHead>
            <TableRow>
              <FeHeadCell sx={{ width: 80 }}>{t("Col-Year")}</FeHeadCell>
              <FeHeadCell align="right" sx={{ width: 144 }}>
                {t("Col-EVIC")}
              </FeHeadCell>
              <FeHeadCell align="right" sx={{ width: 144 }}>
                {t("Col-Equity")}
              </FeHeadCell>
              <FeHeadCell align="right" sx={{ width: 144 }}>
                {t("Col-Debt")}
              </FeHeadCell>
              <FeHeadCell align="right" sx={{ width: 144 }}>
                {t("Col-Revenue")}
              </FeHeadCell>
              <FeHeadCell align="center" sx={{ width: 144 }}>
                {t("Col-As-Of")}
              </FeHeadCell>
              <FeHeadCell align="center" sx={{ width: 80 }}>
                {t("Col-Action")}
              </FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((f) => (
              <FeRow key={f.year}>
                <FeCell>{f.year}</FeCell>
                <FeCell align="right">{formatNumber(f.evic)}</FeCell>
                <FeCell align="right">{formatNumber(f.equity)}</FeCell>
                <FeCell align="right">{formatNumber(f.debt)}</FeCell>
                <FeCell align="right">{formatNumber(f.revenue)}</FeCell>
                <FeCell align="center">{formatDate(f.asOfDate)}</FeCell>
                <FeCell align="center">
                  <IconButton
                    size="small"
                    aria-label={t("Edit")}
                    onClick={() => onEditYear(f)}
                    sx={{ color: "var(--green)", p: "2px" }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </FeCell>
              </FeRow>
            ))}
          </TableBody>
        </FeTable>
        </FeTableScroll>
        <FePagination
          count={borrower.financials.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      </FeTableWrap>
    </Box>
  );
};

export default DetailFinancial;
